import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { compact } from 'lodash-es';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { ConfirmationModal, MenuItem, Select, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useOverlayContext, useIoContext, useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { RelationshipStatus } from '~/models/relationship';
import { getClientViewURL } from '~/pages/unAuthorizedPages/LoginScreen';
import { selectors } from '~/store/state-selectors';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
const linksTextsPath = 'pages.agreementsDashboard.shareLinksModal.links';
export const GetShareAgreementLinksModalTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            ShareAgreementLinksModalTID: {
                Modal: 'direct-links-modal',
                Title: 'direct-links-title',
                LinkSelect: 'select-direct-link',
                LinkOption: (linkId)=>"direct-link-".concat(linkId)
            }
        }
    });
function getAgreementLinks(id, isTerminated, shareLinksModalTexts) {
    return compact([
        {
            id: 'home',
            text: shareLinksModalTexts.home(),
            link: routes.home
        },
        {
            id: 'agreement',
            text: shareLinksModalTexts.agreement(),
            link: routes.agreementMinisiteRoutes(id).agreement
        },
        !isTerminated && {
            id: 'pm',
            text: shareLinksModalTexts.pm(),
            link: routes.agreementMinisiteRoutes(id).paymentMethods
        },
        {
            id: 'team',
            text: shareLinksModalTexts.team(),
            link: routes.team
        },
        {
            id: 'business',
            text: shareLinksModalTexts.business(),
            link: routes.businessDetails
        },
        {
            id: 'profile',
            text: shareLinksModalTexts.profile(),
            link: routes.profile
        },
        {
            id: 'invoices',
            text: shareLinksModalTexts.invoices(),
            link: routes.agreementMinisiteRoutes(id).invoices
        },
        {
            id: 'activity',
            text: shareLinksModalTexts.activity(),
            link: routes.agreementMinisiteRoutes(id).activityLog
        }
    ]);
}
export default function ShareAgreementLinksModal(param) {
    let { id, customerName, status, dismissModal } = param;
    const { t: shareLinksModalTexts } = useLocaleKeys('pages.agreementsDashboard.shareLinksModal');
    const { t: linksTexts } = useLocaleKeys(linksTextsPath);
    const { control, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            pathname: ''
        }
    });
    const { browser: { copyToClipboard } } = useIoContext();
    const { showErrorToast, showSuccessToast } = useOverlayContext();
    const isViewMode = useStore(selectors.isViewMode);
    const { ShareAgreementLinksModalTID } = useMemo(()=>GetShareAgreementLinksModalTID(), []);
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'share_agreement_links_confirm'
            },
            secondaryButton: {
                name: 'share_agreement_links_cancel'
            }
        },
        textAlignCenter: false,
        title: shareLinksModalTexts.title(),
        testId: ShareAgreementLinksModalTID.Modal(),
        content: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    testId: ShareAgreementLinksModalTID.Title(),
                    children: shareLinksModalTexts.inviteTo({
                        name: customerName
                    })
                }),
                /*#__PURE__*/ _jsx("br", {}),
                /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "pathname",
                    rules: requiredValidation(),
                    render: (p)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                            bi: {
                                name: 'agreement_share_link_action'
                            },
                            collapseHelperText: false,
                            label: shareLinksModalTexts.chooseActionLabel(),
                            testId: ShareAgreementLinksModalTID.LinkSelect()
                        }, connectControllerToTextField(p)), {
                            children: getAgreementLinks(id, status === RelationshipStatus.Terminated, linksTexts).map((param)=>{
                                let { id: linkId, text, link } = param;
                                return /*#__PURE__*/ _jsx(MenuItem, {
                                    value: getClientViewURL(link),
                                    testId: ShareAgreementLinksModalTID.LinkOption(linkId),
                                    children: text
                                }, link);
                            })
                        }))
                })
            ]
        }),
        primaryButtonLabel: shareLinksModalTexts.copyLink(),
        primaryButtonDisabled: isViewMode,
        secondaryButtonLabel: shareLinksModalTexts.cancel(),
        onSecondaryButtonClick: dismissModal,
        onPrimaryButtonClick: handleSubmit(async (param)=>{
            let { pathname } = param;
            const result = await handlePromise(copyToClipboard(window.location.origin + pathname));
            if (!result.success) {
                showErrorToast(shareLinksModalTexts.copyError(), 'copy-link-error');
            } else {
                showSuccessToast(shareLinksModalTexts.copySuccess(), 'copy-link-success');
                dismissModal();
            }
        })
    });
}
